// Lightweight sessionStorage-backed persistence for live quiz sessions.
// Survives page refreshes (e.g. a student re-opening the link, or the teacher
// reloading the live screen) without needing a global store.

const TEACHER_KEY = (shortId) => `quiz:teacher:${shortId}`;
const STUDENT_KEY = (shortId) => `quiz:student:${shortId}`;

function read(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function write(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage may be unavailable; ignore */
  }
}

/* ------------------------------- Teacher side ------------------------------ */
// session: { uuidQuiz, shortId, secondsPerAnswer, deckName, cards: [...] }
export function saveTeacherSession(session) {
  if (!session?.shortId) return;
  write(TEACHER_KEY(session.shortId), session);
}

export function getTeacherSession(shortId) {
  return read(TEACHER_KEY(shortId));
}

export function updateTeacherSession(shortId, patch) {
  const current = getTeacherSession(shortId) || {};
  const next = { ...current, ...patch };
  write(TEACHER_KEY(shortId), next);
  return next;
}

/* ------------------------------- Student side ------------------------------ */
// session: { shortId, uuidParticipant, name }
export function saveStudentSession(session) {
  if (!session?.shortId) return;
  write(STUDENT_KEY(session.shortId), session);
}

export function getStudentSession(shortId) {
  return read(STUDENT_KEY(shortId));
}

/* --------------------------------- Helpers --------------------------------- */
// Normalize a raw card (from deck cards API or NEXT_QUESTION_EVENT) into a
// render-friendly question object understood by both teacher and student UIs.
export function normalizeCard(card) {
  if (!card) return null;

  const textPart = card.question?.parts?.find((p) => p.contentType?.includes("text"));
  const imagePart = card.question?.parts?.find((p) => p.contentType?.includes("image"));

  const answerParts = card.answers?.[0]?.parts || [];
  const choiceParts = answerParts.filter((p) => p.contentType?.startsWith("test/"));
  const isChoice = card.answerType === "TEST" || choiceParts.length > 0;

  const options = isChoice
    ? choiceParts.map((p) => ({
        text: p.contentBody,
        correct: p.contentType === "test/correct",
      }))
    : [];

  const correctAnswerText = answerParts
    .filter((p) => p.contentType?.includes("text") || p.contentType === "test/correct")
    .map((p) => p.contentBody)
    .join(", ");

  return {
    cardId: card.cardId,
    cardName: card.cardName,
    questionText: textPart?.contentBody || "",
    questionImage: imagePart?.contentBody || "",
    answerType: card.answerType,
    isChoice,
    options,
    correctAnswerText,
  };
}
